import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { MessageCircle, Info, Cpu, ChevronDown, Check, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import ChatUI from '../../components/ChatUI';
import { llmModels } from '../../data/dummyData';

export default function LocalSimulator() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [selectedModel, setSelectedModel] = useState('openai/gpt-4o-mini');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState(null);
  const [isSavingModel, setIsSavingModel] = useState(false);

  const fetchAgent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getAgent(id);
      setAgent(response.agent);
      // Set the selected model from agent data if available
      if (response.agent?.model) {
        setSelectedModel(response.agent.model);
      }
    } catch (err) {
      console.error('Failed to fetch agent:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Fetch chat history from Firestore
  const fetchHistory = useCallback(async () => {
    try {
      const response = await api.getSimulatorHistory(id);
      if (response.messages) {
        setMessages(response.messages);
      }
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchAgent();
    fetchHistory();
  }, [fetchAgent, fetchHistory]);

  const handleSendMessage = async (text) => {
    // Add user message optimistically
    const userMessage = {
      id: `msg-${Date.now()}`,
      type: 'user',
      text,
      time: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);

    try {
      // Call the chat API (which now saves to Firestore)
      const response = await api.chatWithAgent(id, text);

      const aiMessage = {
        id: `msg-${Date.now()}-ai`,
        type: 'ai',
        text: response.response || response.message || 'Sorry, I could not generate a response.',
        time: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = {
        id: `msg-${Date.now()}-error`,
        type: 'ai',
        text: `Error: ${err.message || 'Failed to get response from AI'}`,
        time: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleClear = async () => {
    setIsClearing(true);
    try {
      await api.clearSimulatorHistory(id);
      setMessages([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
    } finally {
      setIsClearing(false);
    }
  };

  const handleModelChange = async (modelId) => {
    setSelectedModel(modelId);
    setShowModelSelector(false);

    // Save to backend
    try {
      setIsSavingModel(true);
      await api.updateAgentModel(id, { model: modelId });
      // Update local agent state
      setAgent(prev => prev ? { ...prev, model: modelId } : prev);
    } catch (err) {
      console.error('Failed to save model:', err);
      // Revert on error
      if (agent?.model) {
        setSelectedModel(agent.model);
      }
    } finally {
      setIsSavingModel(false);
    }
  };

  const currentModel = llmModels.find(m => m.id === selectedModel) || llmModels[0];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchAgent}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Model selector component for ChatUI header
  const modelSelector = (
    <div className="relative">
      <button
        onClick={() => setShowModelSelector(!showModelSelector)}
        disabled={isSavingModel}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-border rounded-lg
                   hover:border-primary/30 transition-colors disabled:opacity-50 text-sm"
      >
        {isSavingModel ? (
          <RefreshCw className="w-3.5 h-3.5 text-primary animate-spin" />
        ) : (
          <Cpu className="w-3.5 h-3.5 text-primary" />
        )}
        <span className="font-medium text-text-primary">{currentModel.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showModelSelector ? 'rotate-180' : ''}`} />
      </button>

      {showModelSelector && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
          <div className="p-3 border-b border-border bg-gray-50">
            <p className="text-xs font-medium text-text-muted uppercase">Change AI Model</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {llmModels.map((model) => (
              <button
                key={model.id}
                onClick={() => handleModelChange(model.id)}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors flex items-start gap-3
                           ${selectedModel === model.id ? 'bg-primary/5' : ''}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0
                               ${selectedModel === model.id ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                  {selectedModel === model.id && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary text-sm">{model.name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${model.badgeColor}`}>{model.badge}</span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">{model.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Local Simulator</h1>
          <p className="text-text-secondary">Private testing sandbox for your AI</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-primary/5 rounded-xl border border-primary/10 flex items-start gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-text-secondary">
            This is a private testing sandbox. Your conversation history is saved so you can continue testing later. Click "Clear" to start fresh.
          </p>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="max-w-3xl h-[600px]">
        <ChatUI
          messages={messages}
          onSendMessage={handleSendMessage}
          onClear={handleClear}
          aiName={agent?.name || 'AI Agent'}
          helperText="Test your AI agent here. Try asking questions your customers might ask!"
          placeholder="Type a test message..."
          headerRight={modelSelector}
          isClearing={isClearing}
        />
      </div>
    </div>
  );
}